import React from 'react';
import {
  Box,
  Typography,
  Button,
  Link
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Customer, CustomerStatus } from '../../services/customerService';

interface CustomerDetailsCardProps {
  customer: Customer;
  title?: string;
  showChangeButton?: boolean;
  onChangeClick?: () => void;
  changeButtonText?: string;
}

const CustomerDetailsCard: React.FC<CustomerDetailsCardProps> = ({
  customer,
  showChangeButton = false,
  onChangeClick,
  changeButtonText = "Change"
}) => {

  const formatAddress = () => {
    if (!customer.address) return null;
    
    const parts = [
      customer.address,
      customer.city,
      customer.state,
      customer.zipCode
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  return (
    <Box sx={{ 
      p: 2, 
      bgcolor: 'primary.50', 
      borderRadius: 1, 
      border: '1px solid', 
      borderColor: '#c4c4c4' 
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
            {customer.fullName}
          </Typography>
          {customer.email?.trim().length > 0 && (
            <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {customer.email}
              </Typography>
            </Box>
          )}
          {customer.phoneNumber?.trim().length > 0 && (
            <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Link 
                href={`tel:${customer.phoneNumber}`}
                variant="body2"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                    cursor: 'pointer'
                  }
                }}
              >
                {customer.phoneNumber}
              </Link>
            </Box>
          )}
          {formatAddress() && (
            <Box display="flex" alignItems="flex-start" sx={{ mb: 0.5 }}>
              <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', mt: 0.1 }} />
              <Typography variant="body2" color="text.secondary">
                {formatAddress()}
              </Typography>
            </Box>
          )}
          {(customer.secondaryContactName || customer.secondaryContactPhone || customer.secondaryContactEmail) && (
            <>
              {customer.secondaryContactName && (
                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {customer.secondaryContactName}
                  </Typography>
                </Box>
              )}
              {customer.secondaryContactPhone && (
                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Link
                    href={`tel:${customer.secondaryContactPhone}`}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.main',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    {customer.secondaryContactPhone}
                  </Link>
                </Box>
              )}
              {customer.secondaryContactEmail && (
                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {customer.secondaryContactEmail}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        {showChangeButton && (
          <Button
            size="small"
            variant="outlined"
            onClick={onChangeClick}
            sx={{ ml: 2, alignSelf: 'flex-start' }}
          >
            {changeButtonText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CustomerDetailsCard;
